/** Thin fetch wrapper with JWT refresh. */

import type { TokenResponse } from "../types";

const API_BASE = import.meta.env.VITE_API_BASE || "/api/v1";

type Listener = (authed: boolean) => void;

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private listeners = new Set<Listener>();

  constructor() {
    this.accessToken = localStorage.getItem("access_token");
    this.refreshToken = localStorage.getItem("refresh_token");
  }

  onAuthChange(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify() {
    const authed = !!this.accessToken;
    this.listeners.forEach((fn) => fn(authed));
  }

  setTokens(tokens: TokenResponse | null) {
    if (!tokens) {
      this.accessToken = null;
      this.refreshToken = null;
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    } else {
      this.accessToken = tokens.access_token;
      this.refreshToken = tokens.refresh_token;
      localStorage.setItem("access_token", tokens.access_token);
      localStorage.setItem("refresh_token", tokens.refresh_token);
    }
    this.notify();
  }

  get isAuthenticated() {
    return !!this.accessToken;
  }

  async request<T>(
    path: string,
    options: RequestInit = {},
    retry = true,
  ): Promise<T> {
    const headers = new Headers(options.headers || {});
    if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (this.accessToken) {
      headers.set("Authorization", `Bearer ${this.accessToken}`);
    }

    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

    if (res.status === 401 && retry && this.refreshToken) {
      const ok = await this.tryRefresh();
      if (ok) return this.request<T>(path, options, false);
      this.setTokens(null);
      throw new Error("Session expired");
    }

    if (!res.ok) {
      let detail = res.statusText;
      try {
        const body = await res.json();
        detail = body.detail || JSON.stringify(body);
        if (Array.isArray(detail)) {
          detail = detail.map((d: { msg?: string }) => d.msg || String(d)).join(", ");
        }
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }

    if (res.status === 204) return undefined as T;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) return res.json();
    return res as unknown as T;
  }

  private async tryRefresh(): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });
      if (!res.ok) return false;
      const tokens: TokenResponse = await res.json();
      this.setTokens(tokens);
      return true;
    } catch {
      return false;
    }
  }

  async login(email: string, password: string) {
    const tokens = await this.request<TokenResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    this.setTokens(tokens);
    return tokens;
  }

  logout() {
    this.setTokens(null);
  }

  /** Download binary (export / receipt file) with auth header. */
  async download(path: string): Promise<Blob> {
    const headers = new Headers();
    if (this.accessToken) {
      headers.set("Authorization", `Bearer ${this.accessToken}`);
    }
    const res = await fetch(`${API_BASE}${path}`, { headers });
    if (!res.ok) throw new Error("Download failed");
    return res.blob();
  }
}

export const api = new ApiClient();
